// ===========================================
// PAGINA: Auditoria del Sistema
// ===========================================

import { useState } from 'react'
import { PageHeader } from '@/components/layout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  RefreshCw,
  Download,
  Activity,
  Users,
  FileText,
  Clock,
  Mail,
} from 'lucide-react'
import { toast } from 'sonner'

import { AuditoriaList, AuditoriaFilters } from '@/components/auditoria'
import { useAuditoria, useExportarAuditoria, useEstadisticasAuditoria } from '@/hooks/useAuditoria'
import type { FiltrosAuditoria } from '@/types/auditoria'
import { getAccionConfig, getEntidadConfig } from '@/types/auditoria'
import { EmailHistorySection } from '@/components/configuracion/EmailHistorySection'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'

export function Auditoria() {
  const { organizacionId } = useOrganizacion()

  // Estado de filtros
  const [filtros, setFiltros] = useState<FiltrosAuditoria>({})
  const [limite, setLimite] = useState(100)

  // Queries
  const { data: logs = [], isLoading, refetch } = useAuditoria(filtros, limite)
  const { data: estadisticas } = useEstadisticasAuditoria(30)
  const exportar = useExportarAuditoria()

  // Handlers
  const handleLimpiarFiltros = () => {
    setFiltros({})
  }

  const handleExportar = async () => {
    try {
      const cantidad = await exportar.mutateAsync({ filtros, limite: 1000 })
      toast.success(`Exportados ${cantidad} registros`)
    } catch {
      toast.error('Error al exportar')
    }
  }

  const handleCargarMas = () => {
    setLimite((prev) => prev + 100)
  }

  // Top acciones y entidades
  const topAcciones = estadisticas
    ? Object.entries(estadisticas.porAccion)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
    : []

  const topEntidades = estadisticas
    ? Object.entries(estadisticas.porEntidad)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5)
    : []

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Auditoría"
        description="Registro de acciones y comunicaciones del sistema"
      />

      <main className="flex-1 p-4 md:p-6 space-y-4 md:space-y-6">
        <Tabs defaultValue="actividad">
          <TabsList>
            <TabsTrigger value="actividad" className="gap-2">
              <Activity className="h-4 w-4" />
              Actividad
            </TabsTrigger>
            <TabsTrigger value="emails" className="gap-2">
              <Mail className="h-4 w-4" />
              Emails
            </TabsTrigger>
          </TabsList>

          <TabsContent value="actividad" className="space-y-4 md:space-y-6 mt-4">
            {/* Barra de acciones */}
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Actualizar</span>
              </Button>
              <Button size="sm" onClick={handleExportar} disabled={exportar.isPending}>
                <Download className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Exportar</span> CSV
              </Button>
            </div>

            {/* Estadisticas */}
            {estadisticas && (
              <div className="grid gap-3 grid-cols-2 md:gap-4 md:grid-cols-4">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Registros (30 dias)
                    </CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {estadisticas.totalRegistros.toLocaleString()}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Usuarios Activos
                    </CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{estadisticas.usuariosActivos}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Top Acciones
                    </CardTitle>
                    <FileText className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1">
                      {topAcciones.map(([accion, count]) => (
                        <Badge
                          key={accion}
                          variant="outline"
                          className={getAccionConfig(accion).bgClass}
                        >
                          {getAccionConfig(accion).label}: {count}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      Top Modulos
                    </CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-1">
                      {topEntidades.map(([entidad, count]) => (
                        <Badge key={entidad} variant="secondary">
                          {getEntidadConfig(entidad).label}: {count}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Filtros */}
            <Card>
              <CardHeader>
                <CardTitle>Filtros</CardTitle>
                <CardDescription>
                  Filtra los registros por fecha, usuario, accion o modulo
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AuditoriaFilters
                  filtros={filtros}
                  onFiltrosChange={setFiltros}
                  onLimpiar={handleLimpiarFiltros}
                />
              </CardContent>
            </Card>

            {/* Lista de logs */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Registros de Auditoria</CardTitle>
                    <CardDescription>
                      Mostrando {logs.length} de {limite} registros maximo
                    </CardDescription>
                  </div>
                  {logs.length >= limite && (
                    <Button variant="outline" onClick={handleCargarMas}>
                      Cargar mas
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <AuditoriaList logs={logs} isLoading={isLoading} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="emails" className="mt-4">
            {organizacionId && (
              <EmailHistorySection organizacionId={organizacionId} />
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}
